<script setup lang="ts">
  // Footer actions of the view workspace (visuals ported from cartadigital-pos
  // PosOrderViewActions.vue). The transferable logic (accept/reject, PIN
  // cancel, print gating) lives in useOrderViewActions — shared with the
  // history order modal. This component keeps the host-specific parts:
  // Editar (converts the tab to edit mode), the hand-rolled print-menu
  // open/close UI, and the auto-close-on-archive/cancel behavior ported from
  // -pos: when the viewed order *transitions* to archived (Finished + fully
  // paid) or Canceled — never on initial load — the tab closes with a
  // "Reabrir" toast. Pay/discount/tip/invoice live inside the re-hosted
  // payment component.
  import { computed, getCurrentInstance, inject, ref, watch, Ref } from 'vue';
  import { useRouter } from 'vue-router/composables';
  import { onClickOutside } from '@vueuse/core';
  import {
    useOrderViewActions,
    IPrintMenuItem,
  } from '@/composition/use-order-view-actions';
  import { usePosNavigation } from '@/composition/use-pos-navigation';
  import { deviceCanEverPrint } from '@/composition/use-printing';
  import { useUi } from '@/composition/use-ui';
  import {
    computeNextOrderAutoCloseBaselineState,
    getInitialOrderAutoCloseBaselineState,
    shouldAutoCloseArchivedTransition,
    shouldAutoCloseCanceledTransition,
  } from '@/domain/pos/order-auto-close-baseline';
  import { PosV2Error } from '@/domain/pos/pos-error';
  import { roundPrice } from '@/utils';
  import { OrderStatus } from '@/enums/OrderEnums';
  import { AlertType } from '@/interfaces/IAlert';
  import { IOrder } from '@/interfaces/IOrder';
  import { usePosDraftsStore } from '@/stores/pos-drafts.store';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';

  const order = inject<Ref<IOrder>>('order')!;

  const ui = useUi();
  const posDrafts = usePosDraftsStore();
  const posNavigation = usePosNavigation();
  const router = useRouter();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const {
    updating,
    isReceived,
    isCanceledOrDeclined,
    canCancel,
    handleAccept,
    handleReject,
    handleCancel,
    printMenuItems,
    handlePrint: runPrint,
  } = useOrderViewActions(order);

  //#region Edit

  function handleEdit() {
    const draft = posDrafts.activeDraft;
    const o = order.value;
    if (!draft || draft.orderId !== o.id) return;

    if (o.status === OrderStatus.Canceled) {
      ui.showToast(AlertType.Info, i18nT('posV2.editBlockedCanceled'));
      return;
    }
    if (o.status === OrderStatus.Declined) {
      ui.showToast(AlertType.Info, i18nT('posV2.editBlockedDeclined'));
      return;
    }
    if (o.status === OrderStatus.Finished) {
      ui.showToast(AlertType.Info, i18nT('posV2.editBlockedFinished'));
      return;
    }

    try {
      posDrafts.loadFromOrder(draft.id, o);
      posDrafts.setMode(draft.id, 'edit');
    } catch (error) {
      ui.serverError(error, i18nT('posV2.editLoadError'));
    }
  }

  //#endregion

  //#region Print menu (hand-rolled open/close UI; printing via composable)

  const printMenuRef = ref<HTMLElement>();
  const isPrintMenuOpen = ref(false);
  // The footer scrolls horizontally (overflow-x), which also clips any
  // absolutely-positioned drop-up. The panel is fixed-positioned from the
  // trigger's rect instead so it can escape the footer.
  const printPanelStyle = ref<Record<string, string>>({});

  onClickOutside(printMenuRef, () => {
    isPrintMenuOpen.value = false;
  });

  function togglePrintMenu() {
    if (!isPrintMenuOpen.value) {
      const rect = printMenuRef.value?.getBoundingClientRect();
      if (rect) {
        printPanelStyle.value = {
          right: `${Math.max(8, window.innerWidth - rect.right)}px`,
          bottom: `${window.innerHeight - rect.top + 8}px`,
        };
      }
    }
    isPrintMenuOpen.value = !isPrintMenuOpen.value;
  }

  function handlePrint(item: IPrintMenuItem) {
    // Host UI concern: close this component's own menu before delegating.
    isPrintMenuOpen.value = false;
    void runPrint(item);
  }

  //#endregion

  //#region Auto-close on archive / cancel (ported from -pos)

  const isPaid = computed(() => {
    const o = order.value;
    return roundPrice((o.total ?? 0) - (o.totalPaid ?? 0)) <= 0;
  });

  const isArchived = computed(
    () => order.value.status === OrderStatus.Finished && isPaid.value
  );

  const isCanceled = computed(
    () => order.value.status === OrderStatus.Canceled
  );

  const autoCloseBaseline = ref(getInitialOrderAutoCloseBaselineState());

  watch(
    () => [
      order.value.id ?? null,
      order.value.total,
      order.value.totalPaid,
      isArchived.value,
      isCanceled.value,
    ],
    () => {
      const o = order.value;
      const isReady =
        Number.isFinite(Number(o.total)) &&
        Number.isFinite(Number(o.totalPaid));
      autoCloseBaseline.value = computeNextOrderAutoCloseBaselineState(
        autoCloseBaseline.value,
        {
          orderId: o.id ?? null,
          isReady,
          isArchived: isArchived.value,
          isCanceled: isCanceled.value,
        }
      );
    },
    { immediate: true, flush: 'sync' }
  );

  function closeTabWithReopenToast(orderId: number, titleKey: string): boolean {
    const draft = posDrafts.activeDraft;
    if (!draft || draft.mode !== 'view' || draft.orderId !== orderId) {
      return false;
    }

    const closed = posDrafts.closeDraft(draft.id, true);
    if (!closed) return false;

    ui.showToast(AlertType.Info, i18nT(titleKey), {
      text: i18nT('posV2.reopen'),
      cb: () => {
        posDrafts
          .openOrderTab({ orderId }, 'view')
          .then(() => {
            if (router.currentRoute.name !== 'restaurantPosOrder') {
              posNavigation.rememberOrderWorkspaceOrigin(
                router.currentRoute.fullPath
              );
              router.push({ name: 'restaurantPosOrder' }).catch(() => {
                // NavigationDuplicated — already there.
              });
            }
          })
          .catch((error) => {
            // PosV2Error carries its own i18n key (e.g. a real 404); anything
            // else surfaces through the standard server-error toast.
            if (error instanceof PosV2Error) {
              ui.showToast(AlertType.Error, i18nT(error.i18nKey, error.params));
            } else {
              ui.serverError(error, i18nT('posV2.orderOpenError'));
            }
          });
      },
    });
    return true;
  }

  watch(isArchived, (next, prev) => {
    const orderId = order.value.id ?? null;
    if (
      !shouldAutoCloseArchivedTransition({
        state: autoCloseBaseline.value,
        orderId,
        next,
        prev,
      })
    ) {
      return;
    }

    closeTabWithReopenToast(orderId!, 'posV2.orderArchivedToast');
  });

  watch(isCanceled, (next, prev) => {
    const orderId = order.value.id ?? null;
    if (
      !shouldAutoCloseCanceledTransition({
        state: autoCloseBaseline.value,
        orderId,
        next,
        prev,
      })
    ) {
      return;
    }

    closeTabWithReopenToast(orderId!, 'posV2.orderCanceledToast');
  });

  //#endregion
</script>

<template>
  <footer class="pos-v2-view-actions">
    <PosUiScrollable
      orientation="horizontal"
      variant="overlay"
      theme="page"
      size="sm"
      :hide-scrollbar="true"
      :arrows="true"
      scroll-class="actions-scroll"
    >
      <!-- Left: Edit + Cancel -->
      <div class="actions-group actions-left">
      <PosUiButton
        v-if="!isCanceledOrDeclined"
        color="primary"
        variant="solid"
        size="lg"
        icon="pencil"
        class="action-btn action-btn--collapsible"
        @click="handleEdit"
      >
        {{ $t('posV2.editOrder') }}
      </PosUiButton>

      <PosUiButton
        v-if="canCancel"
        color="error"
        variant="outline"
        size="lg"
        icon="close-circle-outline"
        class="action-btn action-btn--collapsible"
        :disabled="updating"
        @click="handleCancel"
      >
        {{ $t('posV2.cancelOrder') }}
      </PosUiButton>
    </div>

    <!-- Center: Accept / Reject (Received only) -->
    <div v-if="isReceived" class="actions-group actions-center">
      <PosUiButton
        color="error"
        variant="outline"
        size="lg"
        icon="close"
        class="action-btn"
        :disabled="updating"
        @click="handleReject"
      >
        {{ $t('posV2.reject') }}
      </PosUiButton>
      <PosUiButton
        color="success"
        variant="solid"
        size="lg"
        icon="check"
        class="action-btn"
        :loading="updating"
        @click="handleAccept"
      >
        {{ $t('posV2.accept') }}
      </PosUiButton>
    </div>

    <!-- Right: Print menu (drop-up) -->
    <div class="actions-group actions-right">
      <div v-if="deviceCanEverPrint" ref="printMenuRef" class="print-menu">
        <PosUiButton
          color="neutral"
          variant="outline"
          size="lg"
          icon="printer"
          class="action-btn action-btn--collapsible"
          @click="togglePrintMenu"
        >
          {{ $t('posV2.print') }}
          <PosUiIcon
            name="chevron-up"
            class="print-chevron"
            :class="{ 'is-open': isPrintMenuOpen }"
          />
        </PosUiButton>

        <div
          v-if="isPrintMenuOpen"
          class="print-menu-panel"
          :style="printPanelStyle"
        >
          <button
            v-for="item in printMenuItems"
            :key="item.labelKey"
            type="button"
            class="print-menu-item"
            @click="handlePrint(item)"
          >
            <PosUiIcon :name="item.icon" class="item-icon" />
            <span>{{ $t(item.labelKey) }}</span>
          </button>
        </div>
      </div>
    </div>
    </PosUiScrollable>
  </footer>
</template>

<style scoped lang="scss">
  .pos-v2-view-actions {
    @apply tw-flex-shrink-0 tw-border-t tw-border-neutral-200 tw-bg-white;

    :deep(.actions-scroll) {
      @apply tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2;
      @apply lg:tw-gap-3 lg:tw-px-4 lg:tw-py-3;
    }

    .actions-group {
      @apply tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2 lg:tw-gap-3;

      &.actions-left {
        @apply tw-flex-1 tw-justify-start;
      }

      &.actions-center {
        @apply tw-flex-1 tw-justify-center;
      }

      &.actions-right {
        @apply tw-flex-1 tw-justify-end;
      }
    }

    ::v-deep .action-btn {
      @apply tw-min-h-[48px] tw-whitespace-nowrap lg:tw-min-h-[52px] lg:tw-px-5;
    }

    // Icon-only below lg (matches the -pos compact footer behavior).
    .action-btn--collapsible {
      ::v-deep .pos-ui-button-label {
        @apply tw-hidden lg:tw-inline-flex lg:tw-items-center lg:tw-gap-1.5;
      }
    }

    .print-menu {
      @apply tw-relative;

      .print-chevron {
        @apply tw-text-lg tw-transition-transform;

        &.is-open {
          @apply tw-rotate-180;
        }
      }

      .print-menu-panel {
        @apply tw-fixed tw-z-50 tw-min-w-[190px] tw-overflow-hidden tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-p-1 tw-shadow-lg;

        .print-menu-item {
          @apply tw-flex tw-min-h-[44px] tw-w-full tw-items-center tw-gap-2 tw-rounded-md tw-px-3 tw-text-left tw-text-sm tw-text-neutral-700 tw-transition-colors hover:tw-bg-neutral-50;

          .item-icon {
            @apply tw-text-lg tw-text-neutral-500;
          }
        }
      }
    }
  }
</style>
